import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  FolderOpen,
  Description,
  PictureAsPdf,
  TextSnippet,
  Error,
  CloudDone,
  OpenInNew,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axiosClient from '../api/axiosClient';

interface ProcessedFile {
  id: number;
  originalFileName: string;
  fileType: string;
  fileSize: number;
  nextcloudPath: string;
  nextcloudUrl: string;
  createdAt: Date;
}

interface NifCifGroup {
  nifCif: string;
  fileCount: number;
  files: ProcessedFile[];
}

interface OperationError {
  id: number;
  fileName: string;
  fileType: string;
  fileSize: number;
  extractedNif: string;
  errorType: string;
  errorMessage: string;
  createdAt: Date;
}

interface OperationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  operationId: number;
  operationTitle: string;
}

const OperationDetailDialog: React.FC<OperationDetailDialogProps> = ({
  open,
  onClose,
  operationId,
  operationTitle,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<{
    nifCifGroups: NifCifGroup[];
    errors: OperationError[];
  } | null>(null);
  const [error, setError] = React.useState<string>('');
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const loadData = async () => {
    if (!operationId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axiosClient.get(`/manage-files/operations/${operationId}/processed-files`);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar los detalles');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && operationId) {
      loadData();
    }
  }, [open, operationId]);

  const handleToggleExpand = (nifCif: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(nifCif)) {
      newExpanded.delete(nifCif);
    } else {
      newExpanded.add(nifCif);
    }
    setExpandedRows(newExpanded);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (fileType.includes('word') || fileType.includes('document')) return <Description color="primary" />;
    if (fileType.includes('text')) return <TextSnippet color="action" />;
    return <Description />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fixNextcloudUrl = (url: string): string => {
    try {
      // Si la URL ya está bien formada, devolverla tal cual
      if (!url) return url;
      
      // Intentar parsear la URL
      const urlObj = new URL(url);
      
      // Extraer la ruta del archivo (después de /remote.php/dav/files/username)
      const pathMatch = urlObj.pathname.match(/\/remote\.php\/dav\/files\/([^/]+)(.+)$/);
      if (!pathMatch) {
        return url;
      }
      
      const username = pathMatch[1];
      const filePath = pathMatch[2];
      
      // Decodificar la ruta completa para obtener el texto original
      let decodedPath: string;
      try {
        decodedPath = decodeURIComponent(filePath);
      } catch (e) {
        // Si falla la decodificación, intentar decodificar múltiples veces
        decodedPath = filePath;
        let prevDecoded = '';
        while (prevDecoded !== decodedPath) {
          prevDecoded = decodedPath;
          try {
            decodedPath = decodeURIComponent(decodedPath);
          } catch {
            break;
          }
        }
      }
      
      // Volver a codificar cada segmento por separado
      // Esto asegura que cada parte de la ruta esté correctamente codificada
      const segments = decodedPath.split('/').filter(s => s.length > 0);
      
      // Codificar cada segmento individualmente
      const encodedSegments = segments.map(segment => {
        // Codificar cada segmento individualmente
        return encodeURIComponent(segment);
      });
      
      // Reconstruir la ruta con cada segmento codificado
      const correctedPath = '/' + encodedSegments.join('/');
      
      // Reconstruir la URL completa con la ruta corregida
      const correctedUrl = `${urlObj.origin}/remote.php/dav/files/${username}${correctedPath}${urlObj.search}${urlObj.hash}`;
      
      return correctedUrl;
    } catch (error) {
      // Si hay algún error, devolver la URL original
      console.warn('Error corrigiendo URL de Nextcloud:', error);
      return url;
    }
  };

  const handleOpenFile = (url: string) => {
    console.log('handleOpenFile - URL original:', url);
    const correctedUrl = fixNextcloudUrl(url);
    console.log('handleOpenFile - URL corregida:', correctedUrl);
    window.open(correctedUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { width: '80vw', maxWidth: 1600 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderOpen />
          <Typography variant="h6">
            {t('manageFiles.detail.title')} - {operationTitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && !loading && (
          <Box>
            {/* Archivos procesados exitosamente */}
            {data.nifCifGroups.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudDone color="success" />
                  {t('manageFiles.detail.processedFiles')} ({data.nifCifGroups.length} {t('manageFiles.detail.nifCifGroups')})
                </Typography>

                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('manageFiles.detail.nifCif')}</TableCell>
                        <TableCell align="center">{t('manageFiles.detail.fileCount')}</TableCell>
                        <TableCell align="center">{t('manageFiles.detail.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.nifCifGroups.map((group) => (
                        <React.Fragment key={group.nifCif}>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={group.nifCif}
                                  color="primary"
                                  size="small"
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={group.fileCount}
                                color="success"
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                onClick={() => handleToggleExpand(group.nifCif)}
                                size="small"
                              >
                                {expandedRows.has(group.nifCif) ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0, border: 0 }}>
                              <Collapse in={expandedRows.has(group.nifCif)} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('manageFiles.detail.filesInGroup')} ({group.fileCount}):
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>{t('manageFiles.detail.fileName')}</TableCell>
                                        <TableCell align="center">{t('manageFiles.detail.fileType')}</TableCell>
                                        <TableCell align="center">{t('manageFiles.detail.fileSize')}</TableCell>
                                        <TableCell align="center">{t('manageFiles.detail.nextcloudPath')}</TableCell>
                                        <TableCell align="center">{t('manageFiles.detail.actions')}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {group.files.map((file) => (
                                        <TableRow key={file.id}>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {getFileIcon(file.fileType)}
                                              <Typography variant="body2" noWrap title={file.originalFileName} sx={{ maxWidth: 500 }}>
                                                {file.originalFileName}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Chip
                                              label={file.fileType.split('/').pop()?.toUpperCase() || 'N/A'}
                                              size="small"
                                              variant="outlined"
                                            />
                                          </TableCell>
                                          <TableCell align="center">
                                            <Typography variant="body2" noWrap>
                                              {formatFileSize(file.fileSize)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Typography variant="body2" noWrap title={file.nextcloudPath} sx={{ maxWidth: 800, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {file.nextcloudPath}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            {file.nextcloudUrl && (
                                              <IconButton
                                                size="small"
                                                onClick={() => handleOpenFile(file.nextcloudUrl)}
                                                title={file.nextcloudUrl}
                                              >
                                                <OpenInNew fontSize="small" />
                                              </IconButton>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Errores */}
            {data.errors.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Error color="error" />
                  {t('manageFiles.detail.errors')} ({data.errors.length})
                </Typography>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('manageFiles.detail.fileName')}</TableCell>
                        <TableCell align="center">{t('manageFiles.detail.extractedNif')}</TableCell>
                        <TableCell align="center">{t('manageFiles.detail.errorType')}</TableCell>
                        <TableCell>{t('manageFiles.detail.errorMessage')}</TableCell>
                        <TableCell align="center">{t('manageFiles.detail.createdAt')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.errors.map((error) => (
                        <TableRow key={error.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getFileIcon(error.fileType)}
                              <Typography variant="body2">
                                {error.fileName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {error.extractedNif ? (
                              <Chip
                                label={error.extractedNif}
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={error.errorType}
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {error.errorMessage}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {formatDate(error.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Sin datos */}
            {data.nifCifGroups.length === 0 && data.errors.length === 0 && (
              <Alert severity="info">
                {t('manageFiles.detail.noData')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OperationDetailDialog;
